import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useLanguage } from "../LanguageContext";

export default function SearchAdsMediaOutreach() {
  const { t } = useLanguage();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Close expansion zone when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (expandedSection) {
        const expansionArea = document.querySelector('[data-expanded]');
        const triggerElements = document.querySelectorAll('[data-service-trigger]');
        
        // Check if click is outside expansion area and not on a trigger element
        let isClickOnTrigger = false;
        
        triggerElements.forEach(trigger => {
          if (trigger.contains(event.target as Node)) {
            isClickOnTrigger = true;
          }
        });
        
        if (expansionArea && !expansionArea.contains(event.target as Node) && !isClickOnTrigger) {
          setExpandedSection(null);
        }
      }
    };

    if (expandedSection) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedSection]);

  // Case studies data for each service
  const caseStudiesData = {
    "Search Engine Advertising": {
      title: "Search Engine Advertising Case Studies",
      description: "Strategic search advertising campaigns across major Chinese search platforms",
      campaigns: [
        {
          client: "Luxury Hotel Chain",
          campaign: "Chinese Tourist Acquisition",
          platform: "Baidu, Sogou, 360 Search",
          results: "400% increase in Chinese bookings, 60% lower cost per acquisition",
          duration: "6 months"
        },
        {
          client: "E-commerce Fashion Brand",
          campaign: "Product Search Optimization",
          platform: "Baidu, Shenma Mobile Search",
          results: "250% increase in organic traffic, 180% sales growth",
          duration: "4 months"
        },
        {
          client: "Tourism Board",
          campaign: "Destination Marketing Campaign",
          platform: "Baidu, WeChat Search, Douyin Search",
          results: "5M+ impressions, 300% increase in destination inquiries",
          duration: "3 months"
        }
      ],
      testimonial: "Z Creative Partners' search advertising expertise helped us reach Chinese tourists at the exact moment they're planning their trips. Our booking conversion rate increased by 400%.",
      clientName: "Digital Marketing Director, Southeast Asia Hotels"
    },
    "Landing Page Creation": {
      title: "Landing Page Creation Case Studies",
      description: "High-converting Chinese-optimized landing pages with cultural adaptation",
      campaigns: [
        {
          client: "Healthcare Service Provider",
          campaign: "Chinese-Language Medical Services Page",
          platform: "WeChat Browser, Mobile Safari, Baidu Browser",
          results: "85% conversion rate, 300% increase in Chinese patient inquiries",
          duration: "2 months"
        },
        {
          client: "Financial Services Company",
          campaign: "Investment Product Landing Pages",
          platform: "Mobile-optimized, WeChat compatible",
          results: "70% conversion rate, 250% increase in qualified leads",
          duration: "3 months"
        },
        {
          client: "Education Institution",
          campaign: "Chinese Student Enrollment Pages",
          platform: "Cross-platform optimized",
          results: "90% form completion rate, 400% increase in applications",
          duration: "4 months"
        }
      ],
      testimonial: "Their landing pages perfectly captured Chinese user preferences and cultural nuances. Our conversion rates exceeded all expectations.",
      clientName: "Growth Marketing Manager, International Education Group"
    },
    "SEO": {
      title: "SEO & Search Result Optimization Case Studies", 
      description: "Comprehensive SEO strategies for Chinese search engines and platforms",
      campaigns: [
        {
          client: "Restaurant Group",
          campaign: "Local Chinese SEO Optimization",
          platform: "Baidu Maps, Dianping, WeChat Search",
          results: "Top 3 rankings for 50+ keywords, 200% organic traffic increase",
          duration: "8 months"
        },
        {
          client: "Retail Chain",
          campaign: "E-commerce SEO Strategy",
          platform: "Baidu, 360 Search, Sogou",
          results: "150+ #1 keyword rankings, 300% organic revenue growth",
          duration: "12 months"
        },
        {
          client: "Professional Services Firm",
          campaign: "B2B Chinese SEO Campaign",
          platform: "Baidu, WeChat Articles, Zhihu",
          results: "500% increase in Chinese enterprise inquiries, 180% lead quality improvement",
          duration: "6 months"
        }
      ],
      testimonial: "Z Creative Partners transformed our Chinese search visibility. We now dominate search results for our industry keywords and see consistent high-quality leads from Chinese businesses.",
      clientName: "Marketing Director, Professional Services Asia"
    },
    "PR & Media Outreach": {
      title: "PR & Media Outreach Case Studies",
      description: "Strategic media placement and public relations campaigns targeting Chinese audiences",
      campaigns: [
        {
          client: "Tech Startup",
          campaign: "Chinese Media Launch Campaign",
          platform: "36Kr, TechNode, WeChat Official Accounts",
          results: "50+ media mentions, 2M+ total reach, 300% brand awareness increase",
          duration: "3 months"
        },
        {
          client: "Luxury Brand",
          campaign: "KOL & Media Integration Campaign",
          platform: "Xiaohongshu, Weibo, Fashion Media",
          results: "100+ media features, 10M+ combined reach, 250% sales increase",
          duration: "6 months"
        },
        {
          client: "B2B Software Company",
          campaign: "Industry Thought Leadership Campaign",
          platform: "Industry publications, LinkedIn China, WeChat",
          results: "25+ expert interviews, 500% qualified lead increase, established market authority",
          duration: "9 months"
        }
      ],
      testimonial: "Their media network and relationships in the Chinese market are unparalleled. We achieved more media coverage in 3 months than we had in the previous 2 years.",
      clientName: "PR Director, International Technology Group"
    }
  };

  const handleSectionClick = (sectionName: string) => {
    if (expandedSection === sectionName) {
      setExpandedSection(null); // Collapse if already expanded
    } else {
      setExpandedSection(sectionName); // Expand the clicked section
      
      // Scroll to the expansion area after a short delay to allow animation to start
      setTimeout(() => {
        // Use a more specific selector for the expanded content
        const expandedElement = document.querySelector(`[data-expanded="${sectionName}"]`);
        if (expandedElement) {
          // Scroll to position the top of the expanded box with some offset for the header
          const headerOffset = 200; // Account for fixed header + some padding
          const elementPosition = expandedElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 150); // Slightly longer delay to ensure DOM update
    }
  };

  // Service Card Component that can expand in place with full width
  const ServiceCard = ({ 
    serviceName, 
    title, 
    description, 
    features, 
    delay,
    hideButton = false
  }: { 
    serviceName: string; 
    title: string; 
    description: string; 
    features: string[]; 
    delay: number;
    hideButton?: boolean;
  }) => {
    const isExpanded = expandedSection === serviceName;
    const serviceData = isExpanded ? caseStudiesData[serviceName as keyof typeof caseStudiesData] : null;

    return (
      <motion.div
        className={`bg-white border border-slate-200/60 rounded-xl p-8 hover:shadow-xl transition-all duration-300 group hover:border-cyan-400/30 relative overflow-hidden ${
          isExpanded ? 'col-span-full' : 'cursor-pointer'
        }`}
        data-expanded={isExpanded ? serviceName : undefined}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay }}
        viewport={{ once: true }}
        onClick={() => !isExpanded && handleSectionClick(serviceName)}
        data-service-trigger={serviceName}
        layout
      >
        {!isExpanded ? (
          // Collapsed card content
          <div className="space-y-6">
            <h3 className="text-xl text-foreground group-hover:text-slate-800 transition-colors duration-300">
              {title}
            </h3>
            <p className="text-sm text-muted-foreground">
              {description}
            </p>
            
            <div className="space-y-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3 text-sm text-slate-600">
                  <motion.div
                    className="w-2 h-2 bg-gradient-to-br from-cyan-500 to-slate-700 rounded-full mt-1.5 flex-shrink-0"
                  ></motion.div>
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            {!hideButton && (
              <motion.button
                className="border border-slate-300 text-slate-600 px-6 py-2 rounded-lg hover:border-cyan-400 hover:text-cyan-400 transition-all duration-300 text-sm w-full cursor-pointer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {t('searchAds.viewCaseStudies')}
              </motion.button>
            )}
          </div>
        ) : (
          // Expanded card content - full width
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            {/* Triangle pointer */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
            </div>

            {/* Header */}
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-2xl lg:text-3xl text-foreground mb-2">
                  {serviceData?.title}
                </h3>
                {serviceName !== "Search Engine Advertising" && serviceName !== "Landing Page Creation" && (
                  <p className="text-muted-foreground">
                    {serviceData?.description}
                  </p>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedSection(null);
                }}
                className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6L18 18" />
                </svg>
              </button>
            </div>

            {/* Custom Case Studies */}
            {serviceName === "Search Engine Advertising" ? (
              <div className="space-y-8">
                {/* Divider Line after title */}
                <div className="w-full border-t border-slate-300 my-6"></div>
                
                {/* Case Study 1: Dubai Real Estate */}
                <div className="space-y-8">
                  <h3 className="text-2xl font-semibold text-foreground">
                    {t('searchAds.dubaiRealEstateTitle')}
                  </h3>
                  
                  {/* Three-column layout */}
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    {/* Background */}
                    <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-foreground mb-3">
                        {t('searchAds.backgroundTitle')}
                      </h3>
                      <p className="text-sm text-slate-700 leading-relaxed">
                        {t('searchAds.dubaiBackground')}
                      </p>
                    </div>
                    
                    {/* Our Solution */}
                    <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-foreground mb-3">
                        {t('searchAds.solutionTitle')}
                      </h3>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                          <span className="text-sm text-slate-700">{t('searchAds.dubaiSolution1')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                          <span className="text-sm text-slate-700">{t('searchAds.dubaiSolution2')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                          <span className="text-sm text-slate-700">{t('searchAds.dubaiSolution3')}</span>
                        </li>
                      </ul>
                    </div>
                    
                    {/* Impact Highlights */}
                    <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                      <h3 className="text-lg font-semibold text-foreground mb-3">
                        {t('searchAds.impactTitle')}
                      </h3>
                      <ul className="space-y-2">
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                          <span className="text-sm text-slate-700">{t('searchAds.dubaiImpact1')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                          <span className="text-sm text-slate-700">{t('searchAds.dubaiImpact2')}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                          <span className="text-sm text-slate-700">{t('searchAds.dubaiImpact3')}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Pictures with same height */}
                  <div className="flex gap-6 items-center justify-center">
                    <div className="h-96 overflow-hidden">
                      <img 
                        src="/images/case_studies/search_engine_ad/case_1_pic_1.png" 
                        alt="Dubai Real Estate Case Study 1"
                        className="h-full w-auto object-contain"
                      />
                    </div>
                    <div className="h-96 overflow-hidden">
                      <img 
                        src="/images/case_studies/search_engine_ad/case_1_pic_2.png" 
                        alt="Dubai Real Estate Case Study 2"
                        className="h-full w-auto object-contain"
                      />
                    </div>
                  </div>
                  
                  {/* Divider Line - End of case study 1 */}
                  <div className="w-full border-t border-slate-300"></div>
                </div>

                {/* Case Study 2: Private University */}
                <div className="space-y-8">
                  <h3 className="text-2xl font-semibold text-foreground">
                    {t('searchAds.privateUniversityTitle')}
                  </h3>
                  
                  {/* Single Picture */}
                  <div className="flex justify-center">
                    <div className="max-w-3xl">
                      <img 
                        src="/images/case_studies/search_engine_ad/case_2_pic_1.png" 
                        alt="Private University Case Study"
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  </div>
                  
                  {/* Divider Line - End of case study 2 */}
                  <div className="w-full border-t border-slate-300"></div>
                </div>
              </div>
            ) : serviceName === "Landing Page Creation" ? (
              <div className="space-y-8">
                {/* Divider Line after title */}
                <div className="w-full border-t border-slate-300 my-6"></div>
                
                {/* Case Study 1: Private University in Singapore */}
                <div className="space-y-8">
                  <h3 className="text-2xl font-semibold text-foreground">
                    {t('searchAds.americanSchoolTitle')}
                  </h3>
                  
                  {/* Single Picture */}
                  <div className="flex justify-center">
                    <div className="max-w-3xl">
                      <img 
                        src="/images/case_studies/landing_page/case_1_pic_1.png" 
                        alt="Private University in Singapore Case Study"
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  </div>
                  
                  {/* Divider Line - End of case study 1 */}
                  <div className="w-full border-t border-slate-300"></div>
                </div>

                {/* Case Study 2: Private University in Italy */}
                <div className="space-y-8">
                  <h3 className="text-2xl font-semibold text-foreground">
                    {t('searchAds.shrewsburySchoolTitle')}
                  </h3>
                  
                  {/* Single Picture */}
                  <div className="flex justify-center">
                    <div className="max-w-3xl">
                      <img 
                        src="/images/case_studies/landing_page/case_2_pic_1.png" 
                        alt="Private University in Italy Case Study"
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  </div>
                  
                  {/* Divider Line - End of case study 2 */}
                  <div className="w-full border-t border-slate-300"></div>
                </div>
              </div>
            ) : (
              <>
                {/* Campaigns Grid - for other services */}
                <div className="space-y-6">
                  <h4 className="text-xl text-foreground">Campaign Results</h4>
                  <div className="grid gap-6">
                    {serviceData?.campaigns.map((campaign: any, index: number) => (
                      <motion.div
                        key={index}
                        className="bg-gradient-to-r from-slate-50/50 to-cyan-50/30 border border-slate-200/60 rounded-lg p-6 hover:shadow-md transition-all duration-300"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <h5 className="text-lg text-foreground">{campaign.client}</h5>
                            <p className="text-slate-600">{campaign.campaign}</p>
                            <div className="flex flex-wrap gap-2">
                              {campaign.platform.split(', ').map((platform: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 bg-cyan-400/10 text-cyan-700 rounded-full text-sm"
                                >
                                  {platform}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div className="p-4 bg-white/60 rounded-lg">
                              <p className="text-sm text-muted-foreground mb-1">Results</p>
                              <p className="text-slate-800">{campaign.results}</p>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-muted-foreground">Duration</span>
                              <span className="text-sm text-slate-800">{campaign.duration}</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Testimonial */}
                <motion.div
                  className="bg-gradient-to-br from-slate-800/5 to-cyan-400/5 border border-slate-200/60 rounded-xl p-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex-shrink-0 flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <blockquote className="text-slate-700 italic text-lg leading-relaxed mb-4">
                        "{serviceData?.testimonial}"
                      </blockquote>
                      <cite className="text-slate-600 not-italic">
                        — {serviceData?.clientName}
                      </cite>
                    </div>
                  </div>
                </motion.div>
              </>
            )}

            {/* CTA */}
            <div className="text-center pt-6">
              <motion.button
                className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    const headerOffset = 120;
                    const elementPosition = contactSection.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
                <span className="relative z-10">{t('searchAds.startYourCampaign')}</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden pt-26">
      <main className="relative">
        {/* Hero Section */}
        <section className="pt-16 pb-20 relative overflow-hidden">
          {/* Animated horizontal line */}
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Full Width Text Content - No Image */}
            <motion.div
              className="text-center space-y-8 max-w-5xl mx-auto"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h1 className="text-5xl lg:text-7xl leading-tight text-foreground">
                  <span className="block">
                    <span className="bg-gradient-to-r from-slate-800 via-slate-700 to-cyan-600 bg-clip-text text-transparent">
                      {t('searchAds.heroTitle')}
                    </span>
                  </span>
                </h1>

                <motion.p
                  className="text-xl lg:text-2xl text-muted-foreground leading-relaxed max-w-4xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {t('searchAds.heroDescription')}
                </motion.p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Why This Matters Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Left Content - 3/4 width */}
              <div className="lg:col-span-3">
                <motion.div
                  className="space-y-4 mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl lg:text-5xl text-foreground">
                    {t('searchAds.whyThisMatters')}
                  </h2>
                </motion.div>

                <div className="space-y-6">
                  {[
                    t('searchAds.whyThisMattersPoint1'),
                    t('searchAds.whyThisMattersPoint2'),
                    t('searchAds.whyThisMattersPoint3')
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start space-x-4 group"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.1,
                      }}
                      viewport={{ once: true }}
                    >
                      <motion.div
                        className="w-2 h-2 bg-gradient-to-br from-cyan-500 to-slate-700 rounded-full mt-2.5 flex-shrink-0"
                      ></motion.div>
                      <p className="text-lg text-muted-foreground leading-relaxed">
                        {item}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Right Content - 1/4 width - Did You Know Box */}
              <div className="lg:col-span-1">
                <motion.div
                  className="bg-gradient-to-br from-cyan-50 to-slate-50 border-2 border-cyan-400/30 rounded-xl p-6 h-full"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-foreground">
                        {t('searchAds.didYouKnow')}
                      </h3>
                    </div>
                    <p className="text-md text-muted-foreground leading-relaxed">
                      {t('searchAds.didYouKnowContent')}
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Offer Section */}
        <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="text-center space-y-4 mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('searchAds.whatWeOffer')}
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-slate-700 mx-auto rounded-full"></div>
            </motion.div>

            <div className="flex flex-col gap-6 max-w-6xl mx-auto">
              {/* First row - 3 cards */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    title: t('searchAds.offerPoint1Title'),
                    description: t('searchAds.offerPoint1Desc'),
                    icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
                    color: "cyan"
                  },
                  {
                    title: t('searchAds.offerPoint2Title'),
                    description: t('searchAds.offerPoint2Desc'),
                    icon: "M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9",
                    color: "slate"
                  },
                  {
                    title: t('searchAds.offerPoint3Title'),
                    description: t('searchAds.offerPoint3Desc'),
                    icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
                    color: "cyan"
                  }
                ].map((item, index) => (
                  <div
                    key={index}
                    className="bg-white border border-slate-200/60 rounded-xl p-6 relative overflow-hidden"
                  >
                    <div className="relative">
                      <div className={`w-12 h-12 ${item.color === 'cyan' ? 'bg-gradient-to-br from-cyan-500 to-cyan-600' : 'bg-gradient-to-br from-slate-700 to-slate-800'} rounded-lg flex items-center justify-center mb-4`}>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                        </svg>
                      </div>
                      
                      <h4 className="text-lg font-semibold text-foreground mb-2">
                        {item.title}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Second row - 2 cards centered */}
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto w-full">
                {[
                  {
                    title: t('searchAds.offerPoint4Title'),
                    description: t('searchAds.offerPoint4Desc'),
                    icon: "M13 10V3L4 14h7v7l9-11h-7z",
                    color: "slate"
                  },
                  {
                    title: t('searchAds.offerPoint5Title'),
                    description: t('searchAds.offerPoint5Desc'),
                    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                    color: "cyan"
                  }
                ].map((item, index) => (
                  <div
                    key={index + 3}
                    className="bg-white border border-slate-200/60 rounded-xl p-6 relative overflow-hidden"
                  >
                    <div className="relative">
                      <div className={`w-12 h-12 ${item.color === 'cyan' ? 'bg-gradient-to-br from-cyan-500 to-cyan-600' : 'bg-gradient-to-br from-slate-700 to-slate-800'} rounded-lg flex items-center justify-center mb-4`}>
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                        </svg>
                      </div>
                      
                      <h4 className="text-lg font-semibold text-foreground mb-2">
                        {item.title}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Search Engine Marketing Section */}
        <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="text-left space-y-6 mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('searchAds.searchEngineMarketing')}
              </h2>
              <p className="text-lg text-muted-foreground">
                {t('searchAds.searchEngineMarketingDesc')}
              </p>
            </motion.div>

            {/* Grid with full-width expansions */}
            <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-8">
              {/* Search Engine Advertising */}
              <ServiceCard
                serviceName="Search Engine Advertising"
                title={t('searchAds.searchEngineAdvertising')}
                description={t('searchAds.searchEngineAdvertisingDesc')}
                features={[
                  t('searchAds.baiduSEM'),
                  t('searchAds.mobileSearchOpt'), 
                  t('searchAds.keywordResearch')
                ]}
                delay={0}
              />

              {/* Landing Page Creation */}
              <ServiceCard
                serviceName="Landing Page Creation"
                title={t('searchAds.landingPageCreation')}
                description={t('searchAds.landingPageCreationDesc')}
                features={[
                  t('searchAds.culturalAdaptation'),
                  t('searchAds.mobileFirstDesign'),
                  t('searchAds.wechatOptimization')
                ]}
                delay={0.1}
              />

              {/* SEO & Search Result Optimization */}
              <motion.div
                className="bg-white border border-slate-200/60 rounded-xl p-8 hover:shadow-xl transition-all duration-300 group hover:border-cyan-400/30 relative overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="space-y-6">
                  <h3 className="text-xl text-foreground group-hover:text-slate-800 transition-colors duration-300">
                    {t('searchAds.seoOptimization')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('searchAds.seoOptimizationDesc')}
                  </p>
                  
                  <div className="space-y-3">
                    {[
                      t('searchAds.baiduSEO'),
                      t('searchAds.chineseKeyword'),
                      t('searchAds.technicalSEO')
                    ].map((feature, index) => (
                      <div key={index} className="flex items-start space-x-3 text-sm text-slate-600">
                        <motion.div
                          className="w-2 h-2 bg-gradient-to-br from-cyan-500 to-slate-700 rounded-full mt-1.5 flex-shrink-0"
                        ></motion.div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* PR & Media Outreach Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left Content */}
              <motion.div
                className="space-y-8"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl lg:text-5xl text-foreground">
                  {t('searchAds.prMediaOutreach')}
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {t('searchAds.prMediaOutreachDesc')}
                </p>

                <div className="space-y-4">
                  {[
                    t('searchAds.mediaNetworkAccess'),
                    t('searchAds.strategicMessaging'),
                    t('searchAds.thoughtLeadership')
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start space-x-4 group"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.1,
                      }}
                      viewport={{ once: true }}
                    >
                      <motion.div
                        className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300 relative overflow-hidden"
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-white/30"></div>
                        <span className="absolute inset-0 flex items-center justify-center text-white text-xs">
                          ✓
                        </span>
                      </motion.div>
                      <span className="text-foreground leading-relaxed">
                        {item}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Right Content - PR & Media Outreach Image */}
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="relative w-full h-96 rounded-xl overflow-hidden">
                  <img 
                    src="/images/case_studies/media_pr/case_1_pic_1.png"
                    alt="PR & Media Outreach"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </div>

            {/* PR Expanded Content - handled by the button click handler above */}
          </div>
        </section>

        {/* Service Process Section */}
        <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-left space-y-6 mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('searchAds.serviceProcess')}
              </h2>
            </motion.div>

            <div className="flex flex-col gap-6">
              {[
                {
                  step: "1",
                  title: t('searchAds.processStep1Title'),
                  description: t('searchAds.processStep1Desc'),
                },
                {
                  step: "2", 
                  title: t('searchAds.processStep2Title'),
                  description: t('searchAds.processStep2Desc'),
                },
                {
                  step: "3",
                  title: t('searchAds.processStep3Title'),
                  description: t('searchAds.processStep3Desc'), 
                },
                {
                  step: "4",
                  title: t('searchAds.processStep4Title'), 
                  description: t('searchAds.processStep4Desc'),
                },
                {
                  step: "5",
                  title: t('searchAds.processStep5Title'),
                  description: t('searchAds.processStep5Desc'),
                }
              ].map((process, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4 group"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1,
                  }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-slate-800 to-slate-700 rounded-full flex-shrink-0 flex items-center justify-center transition-transform duration-300 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-cyan-400/20"></div>
                    <span className="text-white text-base font-semibold relative z-10">
                      {process.step}
                    </span>
                  </div>
                  
                  <div className="flex-1 pt-1">
                    <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-slate-800 transition-colors duration-300">
                      {process.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {process.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}